import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  MessageSquarePlus,
  Search,
  ArrowRight,
  Construction,
  Trash2,
  Droplets,
  HeartHandshake,
  ShieldAlert,
  CircleEllipsis,
  FileText,
  ClipboardCheck,
  Send,
  BellRing,
  Hash,
  LayoutList,
  Lock,
  RefreshCw,
  ChevronRight,
  CheckCircle2,
  MapPinned,
  CalendarHeart,
  Lightbulb,
  HandHeart,
} from "lucide-react";
import { LamanAwam } from "../components/LamanAwam";
import { Input } from "../components/ui/input";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { useSeo } from "../lib/seo";

/* ---------------------------------------------------------------- data copy */

/* Enam pintu masuk Portal Komuniti. Susunan mengikut keperluan paling kerap. */
const TINDAKAN_PANTAS = [
  {
    ikon: HeartHandshake,
    tajuk: "Saya Perlukan Bantuan",
    huraian:
      "Bantuan kewangan, kebajikan, kesihatan, pendidikan, pekerjaan, perumahan atau dokumen.",
    cta: "Mohon Bantuan",
    to: "/bantuan",
    testId: "bantuan",
  },
  {
    ikon: MessageSquarePlus,
    tajuk: "Laporkan Masalah",
    huraian:
      "Jalan rosak, longkang tersumbat, lampu jalan, sampah, air atau kemudahan awam di kawasan anda.",
    cta: "Buat Laporan",
    to: "/aduan",
    testId: "aduan",
  },
  {
    ikon: Search,
    tajuk: "Semak Kes Saya",
    huraian:
      "Masukkan nombor rujukan anda untuk melihat status terkini dan langkah seterusnya.",
    cta: "Semak Status",
    to: "/semak",
    testId: "semak",
    biru: true,
  },
  {
    ikon: CalendarHeart,
    tajuk: "Program Komuniti",
    huraian:
      "Program bantuan, latihan kemahiran, kesihatan, belia dan aktiviti kampung yang terbuka.",
    cta: "Lihat Program",
    to: "/program",
    testId: "program",
    biru: true,
  },
  {
    ikon: Lightbulb,
    tajuk: "Kongsi Cadangan",
    huraian:
      "Ada idea untuk memperbaiki kawasan kita? Kongsikan dengan kami, boleh tanpa nama.",
    cta: "Hantar Cadangan",
    to: "/cadangan",
    testId: "cadangan",
    biru: true,
  },
  {
    ikon: HandHeart,
    tajuk: "Jadi Sukarelawan",
    huraian:
      "Sumbang masa dan kemahiran anda kepada komuniti mengikut minat dan masa lapang.",
    cta: "Saya Mahu Membantu",
    to: "/sukarelawan",
    testId: "sukarelawan",
  },
];

const KATEGORI_BANTUAN = [
  {
    ikon: Construction,
    nama: "Infrastruktur",
    copy: "Jalan rosak, longkang, lampu jalan dan kemudahan awam.",
  },
  {
    ikon: Trash2,
    nama: "Kebersihan",
    copy: "Sampah, pencemaran, kawasan terbiar dan kebersihan persekitaran.",
  },
  {
    ikon: Droplets,
    nama: "Utiliti",
    copy: "Gangguan air, elektrik atau kemudahan asas yang memerlukan rujukan.",
  },
  {
    ikon: HeartHandshake,
    nama: "Kebajikan",
    copy: "Permohonan bantuan dan isu kebajikan yang memerlukan semakan lanjut.",
  },
  {
    ikon: ShieldAlert,
    nama: "Kesihatan dan Keselamatan",
    copy: "Isu komuniti bukan kecemasan yang memerlukan perhatian pihak berkaitan.",
  },
  {
    ikon: CircleEllipsis,
    nama: "Lain-lain",
    copy: "Isu setempat yang tidak termasuk dalam kategori di atas.",
  },
];

const LANGKAH = [
  {
    ikon: FileText,
    tajuk: "Hantar laporan",
    copy: "Terangkan masalah, pilih lokasi dan sertakan gambar atau dokumen jika berkaitan.",
  },
  {
    ikon: ClipboardCheck,
    tajuk: "Pasukan membuat semakan",
    copy: "Maklumat disemak, dikategorikan dan dipadankan dengan kawasan serta saluran tindakan yang sesuai.",
  },
  {
    ikon: Send,
    tajuk: "Isu dirujuk atau ditugaskan",
    copy: "Pasukan menjalankan susulan atau membawa isu kepada agensi berkaitan mengikut bidang kuasa.",
  },
  {
    ikon: BellRing,
    tajuk: "Semak perkembangan",
    copy: "Gunakan nombor rujukan yang diberikan untuk melihat kemas kini laporan anda.",
  },
];

const PERINGKAT = [
  "Diterima",
  "Disemak",
  "Dirujuk Agensi",
  "Dalam Kerja",
  "Selesai",
];

const PRINSIP = [
  {
    ikon: Hash,
    tajuk: "Direkod",
    copy: "Setiap laporan yang lengkap menerima nombor rujukan untuk semakan.",
  },
  {
    ikon: LayoutList,
    tajuk: "Disusun",
    copy: "Isu dikategorikan mengikut kawasan, jenis masalah dan tahap keutamaan.",
  },
  {
    ikon: Lock,
    tajuk: "Dilindungi",
    copy: "Maklumat peribadi hanya digunakan untuk pengurusan laporan dan tindakan susulan.",
  },
  {
    ikon: RefreshCw,
    tajuk: "Dikemas kini",
    copy: "Status dikemas kini apabila terdapat perkembangan yang dapat disahkan.",
  },
];

const SOALAN = [
  {
    t: "Siapa yang boleh menghantar laporan?",
    j: "Penduduk dan komuniti yang mempunyai isu berkaitan P.061 Padang Rengas boleh menggunakan borang ini. Pastikan lokasi dan maklumat yang diberikan adalah tepat.",
  },
  {
    t: "Adakah setiap laporan pasti dapat diselesaikan?",
    j: "Pasukan akan menyemak dan mengusahakan tindakan susulan yang bersesuaian. Hasil dan tempoh penyelesaian bergantung pada bukti, bidang kuasa, proses serta keputusan agensi yang terlibat.",
  },
  {
    t: "Berapa lama proses semakan mengambil masa?",
    j: "Tempohnya berbeza mengikut jenis dan kerumitan isu. Status dikemas kini apabila terdapat perkembangan yang dapat disahkan.",
  },
  {
    t: "Bolehkah saya membuat laporan tanpa mendedahkan identiti?",
    j: "Maklumat hubungan mungkin diperlukan untuk pengesahan dan tindakan susulan. Borang aduan menerangkan medan yang wajib, tujuan pengumpulan dan cara data dilindungi.",
  },
  {
    t: "Bagaimana saya menyemak laporan?",
    j: "Selepas penghantaran berjaya, anda akan menerima nombor rujukan penuh. Gunakan nombor tersebut pada halaman Semak Status.",
  },
  {
    t: "Adakah platform ini untuk kecemasan?",
    j: "Tidak. Jika berlaku kecemasan atau ancaman terhadap keselamatan dan nyawa, hubungi 999 atau agensi kecemasan berkaitan dengan segera.",
  },
];

/* ------------------------------------------------------------ gaya bersama */

const GALERI = [
  {
    fail: "sumbangan-surau",
    tinggi: 1359,
    tajuk: "Sumbangan untuk surau kampung",
    copy: "Penyampaian sumbangan tunai bagi sistem PA Surau Al-Hussinniah, Kampung Sendayan Baru.",
    besar: true,
  },
  {
    fail: "bantuan-kasih-madani",
    tinggi: 806,
    tajuk: "Bantuan sampai ke rumah",
    copy: "Penyerahan bantuan Kasih Madani kepada penerima di rumah mereka.",
  },
  {
    fail: "mendengar-warga-emas",
    tinggi: 795,
    tajuk: "Mendengar dahulu",
    copy: "Sesi mendengar masalah bersama warga emas di ruang tamu rumah penduduk.",
  },
  {
    fail: "lawatan-rumah",
    tinggi: 800,
    tajuk: "Turun ke kampung",
    copy: "Lawatan dari rumah ke rumah untuk menyemak keadaan penduduk.",
  },
  {
    fail: "perbualan-malam",
    tinggi: 786,
    tajuk: "Waktu malam pun jalan",
    copy: "Perbualan bersama penduduk di kawasan kedai selepas waktu kerja.",
  },
];

const BTN_UTAMA =
  "inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl bg-[color:var(--maroon)] px-5 text-sm font-semibold text-white shadow-sm transition-colors duration-200 hover:bg-[color:var(--maroon-gelap)] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--focus)] focus-visible:ring-offset-2";
const BTN_KEDUA =
  "inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl border border-[color:var(--hairline)] bg-white px-5 text-sm font-semibold text-[color:var(--ink)] transition-colors duration-200 hover:border-[color:var(--brand-sky)] hover:bg-[#F2F9FE] hover:text-[color:var(--brand-blue)] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--focus)] focus-visible:ring-offset-2";
const KAD = "rounded-2xl border border-[color:var(--hairline)] bg-white";
const EYEBROW_LP =
  "text-[11px] font-semibold uppercase tracking-[0.18em] text-[color:var(--maroon)]";
const LENCANA_IKON =
  "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[rgba(140,18,25,0.10)] text-[color:var(--maroon)]";
const LENCANA_BIRU =
  "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[rgba(0,160,233,0.12)] text-[color:var(--brand-blue)]";
/* Butang biru untuk aliran semakan status, berpasangan dengan butang maroon aduan */
const BTN_BIRU =
  "inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl bg-[color:var(--brand-blue)] px-5 text-sm font-semibold text-white shadow-sm transition-colors duration-200 hover:bg-[#00558C] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--focus)] focus-visible:ring-offset-2";

/* Pendedahan lembut apabila skrol tanpa pustaka animasi tambahan.
   Guna IntersectionObserver + peralihan CSS supaya bundle laman awam kekal kecil.
   Tetapan prefers-reduced-motion dihormati melalui CSS. */
const Dedah = ({ children, delay = 0, className = "", sebagai: Tag = "div", ...lain }) => {
  const [tampak, setTampak] = useState(false);
  const ruj = useRef(null);

  useEffect(() => {
    const el = ruj.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === "undefined") {
      setTampak(true);
      return undefined;
    }
    const pengamat = new IntersectionObserver(
      (entri) => {
        if (entri[0].isIntersecting) {
          setTampak(true);
          pengamat.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: "0px 0px -8% 0px" }
    );
    pengamat.observe(el);
    return () => pengamat.disconnect();
  }, []);

  return (
    <Tag
      ref={ruj}
      className={`dedah${tampak ? " tampak" : ""} ${className}`}
      style={delay ? { transitionDelay: `${delay}s` } : undefined}
      {...lain}
    >
      {children}
    </Tag>
  );
};

const Seksyen = ({ id, children, className = "" }) => (
  <section id={id} className={`mx-auto max-w-6xl px-4 sm:px-6 ${className}`}>
    {children}
  </section>
);

const Jalur = ({ className = "" }) => (
  <div className={`jalur-pkr h-[3px] w-24 rounded-full ${className}`} aria-hidden="true" />
);

/* Peta abstrak kawasan sebagai tanda air hero. Bukan peta tepat. */
const TandaAirKawasan = () => (
  <svg
    viewBox="0 0 320 320"
    className="pointer-events-none absolute left-0 top-0 h-full w-full opacity-[0.10]"
    preserveAspectRatio="xMidYMid slice"
    aria-hidden="true"
  >
    <defs>
      <pattern id="grid-lp" width="26" height="26" patternUnits="userSpaceOnUse">
        <path d="M26 0H0V26" fill="none" stroke="#0072BC" strokeWidth="0.6" />
      </pattern>
    </defs>
    <rect width="320" height="320" fill="url(#grid-lp)" />
    <path
      d="M158 24 196 42 214 84 246 108 238 156 258 196 226 240 178 258 138 292 104 258 78 214 92 168 70 126 96 78 128 46Z"
      fill="none"
      stroke="#072F4A"
      strokeWidth="2.5"
      strokeLinejoin="round"
    />
    <path d="M92 168 238 156" fill="none" stroke="#0072BC" strokeWidth="1.6" strokeDasharray="6 5" />
  </svg>
);

/* ------------------------------------------------------------------- halaman */

export default function Landing() {
  useSeo({
    tajuk: "Khidmat Rakyat Kamil Munim",
    keterangan:
      "Salurkan aduan dan isu komuniti P.061 Padang Rengas. Setiap laporan direkod, disemak dan boleh disemak semula menggunakan nombor rujukan.",
    laluan: "/",
  });
  const navigate = useNavigate();
  const [rujukan, setRujukan] = useState("");
  const [cariSoalan, setCariSoalan] = useState("");

  const semak = (e) => {
    e.preventDefault();
    const r = rujukan.trim().toUpperCase();
    navigate(r ? `/semak?rujukan=${encodeURIComponent(r)}` : "/semak");
  };

  const soalanTapis = useMemo(() => {
    const q = cariSoalan.trim().toLowerCase();
    if (!q) return SOALAN;
    return SOALAN.filter((s) => `${s.t} ${s.j}`.toLowerCase().includes(q));
  }, [cariSoalan]);

  return (
    <LamanAwam>
      {/* ------------------------------------------------------------- HERO */}
      <div
        id="utama"
        className="bintik-halus overflow-hidden border-b border-[color:var(--hairline)]"
        style={{
          backgroundImage:
            "linear-gradient(180deg, rgba(140,18,25,0.07) 0%, rgba(255,255,255,1) 55%)",
        }}
      >
        <Seksyen className="py-14 sm:py-20">
          <div className="grid items-center gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-14">
            <div>
              <p className={EYEBROW_LP} data-testid="hero-eyebrow">
                Platform Khidmat Rakyat P.061 Padang Rengas
              </p>
              <h1
                className="font-display mt-4 text-4xl font-semibold leading-[1.06] tracking-tight text-[color:var(--brand-navy)] sm:text-5xl lg:text-6xl"
                data-testid="hero-tajuk"
              >
                Masalah Rakyat,{" "}
                <span className="underline decoration-[color:var(--maroon-terang)] decoration-4 underline-offset-[6px]">
                  Kita Urus Bersama
                </span>
                .
              </h1>
              <Jalur className="mt-6" />
              <p className="mt-6 max-w-xl text-sm leading-relaxed text-[color:var(--ink-muted)] sm:text-base">
                Salurkan aduan dan isu komuniti dengan lebih mudah. Pasukan kami merekod, menyemak
                dan menyalurkan laporan anda kepada pihak yang berkaitan. Anda juga boleh mengikuti
                perkembangan laporan menggunakan nombor rujukan yang diberikan.
              </p>

              <div className="mt-9 flex flex-col items-stretch gap-3 sm:items-center">
                <div className="w-full sm:w-auto">
                  <Link
                    to="/aduan"
                    className="inline-flex min-h-[62px] w-full items-center justify-center gap-2.5 rounded-2xl bg-[color:var(--maroon)] px-10 text-base font-bold text-white shadow-[0_10px_24px_rgba(140,18,25,0.28)] transition-colors duration-200 hover:bg-[color:var(--maroon-gelap)] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--focus)] focus-visible:ring-offset-2 sm:w-auto sm:text-lg"
                    data-testid="hero-cta-aduan"
                  >
                    <MessageSquarePlus className="h-5 w-5" /> Hantar Aduan
                  </Link>
                </div>
                <a
                  href="#semak-status"
                  className={`${BTN_KEDUA} w-full sm:w-auto`}
                  data-testid="hero-cta-semak"
                >
                  <Search className="h-4 w-4" /> Semak Status
                </a>
              </div>

            </div>

            {/* Komposisi kanan: tanda air kawasan dan kad contoh */}
            <div className="relative" data-testid="hero-visual">
              <div
                className="pointer-events-none absolute -right-8 -top-10 h-56 w-56 rounded-full"
                style={{
                  background:
                    "radial-gradient(closest-side, rgba(140,18,25,0.16), rgba(140,18,25,0) 70%)",
                }}
                aria-hidden="true"
              />
              <TandaAirKawasan />

              <div className="relative mx-auto max-w-sm">
                <Dedah className="kad-hero p-4">
                  <p className={EYEBROW_LP}>Peringkat laporan</p>
                  <ol className="mt-3 space-y-2" data-testid="hero-tracker-pratonton">
                    {PERINGKAT.map((p, i) => (
                      <li key={p} className="flex items-center gap-2.5">
                        <span
                          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${
                            i < 2
                              ? "bg-[color:var(--brand-blue)] text-white"
                              : "border border-[color:var(--hairline)] bg-white text-[#6B7280]"
                          }`}
                        >
                          {i < 2 ? <CheckCircle2 className="h-3 w-3" /> : i + 1}
                        </span>
                        <span
                          className={`text-xs ${
                            i < 2
                              ? "font-semibold text-[color:var(--ink)]"
                              : "text-[color:var(--ink-muted)]"
                          }`}
                        >
                          {p}
                        </span>
                      </li>
                    ))}
                  </ol>
                  <p className="mt-3 text-[10px] leading-relaxed text-[#6B7280]">
                    Contoh paparan peringkat. Status sebenar laporan anda dilihat pada halaman Semak
                    Status.
                  </p>
                </Dedah>

                <Dedah delay={0.08} className="kad-hero relative z-10 mt-4 p-5 sm:ml-14">
                  <div className="flex items-center gap-3">
                    <span className={LENCANA_BIRU}>
                      <MessageSquarePlus className="h-[18px] w-[18px]" />
                    </span>
                    <div className="min-w-0">
                      <p className="font-display text-sm font-bold text-[color:var(--ink)]">
                        Borang aduan ringkas
                      </p>
                      <p className="text-[11px] text-[color:var(--ink-muted)]">
                        Isu, lokasi dan gambar
                      </p>
                    </div>
                  </div>
                  <div className="mt-4 space-y-2">
                    {["Pilih kategori isu", "Tandakan lokasi", "Terima nombor rujukan"].map((t) => (
                      <div key={t} className="flex items-center gap-2">
                        <MapPinned className="h-3.5 w-3.5 shrink-0 text-[color:var(--brand-sky)]" />
                        <span className="text-xs text-[color:var(--ink-muted)]">{t}</span>
                      </div>
                    ))}
                  </div>
                  <Jalur className="mt-4" />
                </Dedah>
              </div>
            </div>
          </div>
        </Seksyen>
      </div>

      {/* ------------------------------------------------ TINDAKAN PANTAS
          Enam pintu masuk utama Portal Komuniti. Kad besar, mesra telefon,
          bahasa harian tanpa jargon kerajaan. */}
      <Seksyen id="tindakan" className="py-14 sm:py-16">
        <Dedah>
          <p className={EYEBROW_LP}>Pusat Khidmat Komuniti Padang Rengas</p>
          <h2 className="font-display mt-4 max-w-2xl text-2xl font-semibold leading-tight tracking-tight text-[color:var(--ink)] sm:text-3xl">
            Ada sesuatu yang kami boleh bantu?
          </h2>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-[color:var(--ink-muted)] sm:text-base">
            Pilih satu daripada enam pintu masuk di bawah. Semuanya boleh dibuat menggunakan telefon
            dan tanpa perlu membuka akaun.
          </p>
        </Dedah>

        <div
          className="mt-8 grid gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3"
          data-testid="landing-tindakan-pantas"
        >
          {TINDAKAN_PANTAS.map((t, i) => (
            <Dedah key={t.to} delay={i * 0.04}>
              <Link
                to={t.to}
                data-testid={`tindakan-pantas-${t.testId}`}
                className={`${KAD} group flex h-full min-h-[188px] flex-col p-5 transition-transform duration-200 hover:-translate-y-1 hover:border-[color:var(--brand-sky)] hover:shadow-[0_12px_28px_rgba(7,47,74,0.10)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--focus)] focus-visible:ring-offset-2`}
              >
                <span className={t.biru ? LENCANA_BIRU : LENCANA_IKON}>
                  <t.ikon className="h-[18px] w-[18px]" />
                </span>
                <h3 className="font-display mt-4 text-lg font-bold leading-snug text-[color:var(--ink)]">
                  {t.tajuk}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-[color:var(--ink-muted)]">
                  {t.huraian}
                </p>
                <span
                  className={`mt-auto inline-flex items-center gap-1.5 pt-4 text-sm font-semibold ${
                    t.biru ? "text-[color:var(--brand-blue)]" : "text-[color:var(--maroon)]"
                  }`}
                >
                  {t.cta}
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                </span>
              </Link>
            </Dedah>
          ))}
        </div>
      </Seksyen>

      {/* ------------------------------------------------------ PENGENALAN */}
      <Seksyen className="py-14 sm:py-16">
        <Dedah>
          <div className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr] lg:gap-14">
            <h2 className="font-display text-2xl font-semibold leading-tight tracking-tight text-[color:var(--ink)] sm:text-3xl">
              Satu saluran untuk suara komuniti Padang Rengas
            </h2>
            <p className="text-sm leading-relaxed text-[color:var(--ink-muted)] sm:text-base">
              Masalah setempat kadangkala sukar disalurkan kepada pihak yang betul. Platform ini
              membantu penduduk P.061 Padang Rengas merekod isu komuniti, melampirkan maklumat
              berkaitan dan mengikuti tindakan susulan dalam satu tempat. Setiap laporan disemak
              sebelum dikategorikan dan dirujuk kepada pasukan atau agensi yang bersesuaian. Tempoh
              tindakan bergantung pada jenis isu dan bidang kuasa pihak yang terlibat.
            </p>
          </div>
        </Dedah>
      </Seksyen>

      {/* --------------------------------------------------- KAMIL MUNIM */}
      <div className="tona-awam border-y border-[color:var(--hairline)]">
        <Seksyen className="py-14 sm:py-16">
          <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:gap-14">
            <Dedah>
              <p className={EYEBROW_LP}>Mendengar, menyusun dan mengusahakan</p>
              <h2 className="font-display mt-4 text-2xl font-semibold leading-tight tracking-tight text-[color:var(--ink)] sm:text-3xl">
                Khidmat rakyat yang lebih tersusun dan mudah diikuti
              </h2>
              <p className="mt-5 text-sm leading-relaxed text-[color:var(--ink-muted)] sm:text-base">
                Muhammad Kamil Abdul Munim ialah{" "}
                <strong className="text-[color:var(--ink)]">
                  Setiausaha Politik dan Ketua AMK Malaysia
                </strong>
                . Melalui platform ini, beliau bersama pasukan khidmat rakyat menyediakan saluran
                yang lebih teratur untuk mendengar masalah komuniti P.061 Padang Rengas dan membantu
                membawa isu kepada pihak berkaitan.
              </p>
              <p className="mt-4 text-sm leading-relaxed text-[color:var(--ink-muted)]">
                Platform ini bukan pengganti kepada agensi kerajaan atau perkhidmatan kecemasan. Ia
                berfungsi sebagai saluran pengumpulan, penyelarasan dan susulan isu rakyat.
              </p>
              <div className="mt-8 inline-block">
                <Link to="/aduan" className={BTN_UTAMA} data-testid="kamil-cta-aduan">
                  Laporkan Isu Anda <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </Dedah>

            <Dedah delay={0.08}>
              <figure className={`${KAD} overflow-hidden`} data-testid="kamil-gambar">
                <img
                  src="/gambar/mendengar-warga-emas-720.webp"
                  srcSet="/gambar/mendengar-warga-emas-320.webp 320w, /gambar/mendengar-warga-emas-480.webp 480w, /gambar/mendengar-warga-emas-720.webp 720w, /gambar/mendengar-warga-emas.webp 1000w"
                  sizes="(max-width: 1024px) 94vw, 480px"
                  width={1000}
                  height={795}
                  alt="Kamil Munim berbual bersama seorang warga emas di ruang tamu rumah penduduk"
                  loading="lazy"
                  decoding="async"
                  className="aspect-[4/3] w-full object-cover"
                />
              </figure>
              <div className={`${KAD} mt-4 self-start p-6`} data-testid="kad-fakta-kawasan">
                <Jalur />
                <dl className="mt-5 divide-y divide-[#F0F2F5]">
                  {[
                    ["Kawasan", "P.061 Padang Rengas, Perak"],
                    ["Daerah pentadbiran", "Kuala Kangsar"],
                    ["DUN dalam kawasan", "N.19 Chenderoh, N.20 Lubok Merbau"],
                    ["Saluran utama", "Borang aduan dan semakan status"],
                  ].map(([k, v]) => (
                    <div
                      key={k}
                      className="flex flex-col gap-1 py-3 sm:flex-row sm:justify-between sm:gap-6"
                    >
                      <dt className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#6B7280]">
                        {k}
                      </dt>
                      <dd className="text-sm font-semibold text-[color:var(--ink)] sm:text-right">
                        {v}
                      </dd>
                    </div>
                  ))}
                </dl>
              </div>
            </Dedah>
          </div>
        </Seksyen>
      </div>

      {/* ---------------------------------------------- GALERI LAPANGAN */}
      <Seksyen id="lapangan" className="py-14 sm:py-16">
        <Dedah>
          <p className={EYEBROW_LP}>Khidmat di lapangan</p>
          <h2 className="font-display mt-3 text-2xl font-semibold tracking-tight text-[color:var(--ink)] sm:text-3xl">
            Kerja yang dibuat, bukan sekadar dijanji
          </h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-[color:var(--ink-muted)]">
            Sebahagian aktiviti khidmat rakyat di sekitar kawasan. Setiap lawatan menjadi sumber
            maklumat untuk laporan dan tindakan susulan.
          </p>
        </Dedah>

        <div
          className="mt-8 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4"
          data-testid="landing-galeri"
        >
          {GALERI.map((g, i) => (
            <Dedah
              key={g.fail}
              delay={i * 0.06}
              className={g.besar ? "col-span-2 lg:row-span-2" : ""}
            >
              <figure
                className={`${KAD} group h-full overflow-hidden`}
                data-testid={`galeri-item-${i}`}
              >
                <div
                  className={`overflow-hidden bg-[color:var(--surface-tint-2)] ${
                    g.besar ? "aspect-[4/5] lg:aspect-[3/4]" : "aspect-square"
                  }`}
                >
                  <img
                    src={`/gambar/${g.fail}-480.webp`}
                    srcSet={`/gambar/${g.fail}-320.webp 320w, /gambar/${g.fail}-480.webp 480w, /gambar/${g.fail}-720.webp 720w, /gambar/${g.fail}.webp 1000w`}
                    sizes={
                      g.besar
                        ? "(max-width: 1024px) 94vw, 560px"
                        : "(max-width: 1024px) 46vw, 275px"
                    }
                    width={1000}
                    height={g.tinggi}
                    alt={g.copy}
                    loading={i === 0 ? "eager" : "lazy"}
                    fetchPriority={i === 0 ? "high" : "auto"}
                    decoding="async"
                    className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-[1.03]"
                  />
                </div>
                <figcaption className="p-4">
                  <p className="font-display text-sm font-bold text-[color:var(--ink)]">
                    {g.tajuk}
                  </p>
                  <p className="mt-1 text-xs leading-relaxed text-[color:var(--ink-muted)]">
                    {g.copy}
                  </p>
                </figcaption>
              </figure>
            </Dedah>
          ))}
        </div>

        <Dedah>
          <p className="mt-4 text-[11px] leading-relaxed text-[#6B7280]" data-testid="galeri-nota">
            Gambar aktiviti khidmat rakyat pasukan Kamil Munim. Sebahagian program dilaksanakan
            bersama agensi dan penduduk setempat.
          </p>
        </Dedah>
      </Seksyen>

      {/* -------------------------------------------------- JENIS BANTUAN */}
      <Seksyen id="bantuan" className="py-14 sm:py-16">
        <Dedah>
          <p className={EYEBROW_LP}>Jenis bantuan</p>
          <h2 className="font-display mt-3 text-2xl font-semibold tracking-tight text-[color:var(--ink)] sm:text-3xl">
            Apakah isu yang boleh dilaporkan?
          </h2>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-[color:var(--ink-muted)]">
            Pilih kategori yang paling hampir dengan masalah anda. Pasukan kami akan menyemak dan
            membantu menentukan saluran tindakan yang sesuai.
          </p>
        </Dedah>

        <div
          className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3"
          data-testid="landing-category-grid"
        >
          {KATEGORI_BANTUAN.map((k, i) => (
            <Dedah key={k.nama} delay={i * 0.06}>
              <div className="group h-full">
                <Link
                  to="/aduan"
                  data-testid={`kategori-kad-${i}`}
                  className={`${KAD} card-hoverable flex h-full items-start gap-3 p-4 transition-colors duration-200 hover:border-slate-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--focus)] sm:p-5`}
                >
                  <span className={LENCANA_IKON}>
                    <k.ikon className="h-[18px] w-[18px]" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="font-display block text-base font-bold text-[color:var(--ink)]">
                      {k.nama}
                    </span>
                    <span className="mt-1 block text-sm leading-relaxed text-[color:var(--ink-muted)]">
                      {k.copy}
                    </span>
                  </span>
                  <ChevronRight className="mt-1 h-4 w-4 shrink-0 text-[#8FA3B4] transition-transform duration-200 group-hover:translate-x-0.5" />
                </Link>
              </div>
            </Dedah>
          ))}
        </div>

        <Dedah>
          <div className="mt-6 rounded-xl border border-[color:var(--warm-accent-2)] bg-[#FFFBF6] px-4 py-3">
            <p
              className="text-xs leading-relaxed text-[#8A5A22]"
              data-testid="notis-tiada-jaminan"
            >
              Penghantaran laporan tidak menjamin kelulusan bantuan atau penyelesaian serta merta.
              Setiap kes tertakluk pada semakan, bukti dan bidang kuasa agensi yang berkenaan.
            </p>
          </div>
        </Dedah>
      </Seksyen>

      {/* ------------------------------------------------ CARA IA BERFUNGSI */}
      <div className="tona-krim border-y border-[color:var(--hairline)]">
        <Seksyen id="cara" className="py-14 sm:py-16">
          <Dedah>
            <p className={EYEBROW_LP}>Cara ia berfungsi</p>
            <h2 className="font-display mt-3 text-2xl font-semibold tracking-tight text-[color:var(--ink)] sm:text-3xl">
              Daripada laporan kepada tindakan susulan
            </h2>
          </Dedah>

          <ol className="mt-8 grid gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4" data-testid="landing-how-it-works">
            {LANGKAH.map((l, i) => (
              <Dedah
                key={l.tajuk}
                delay={i * 0.06}
                sebagai="li"
                className={`${KAD} h-full p-5`}
                data-testid={`langkah-${i}`}
              >
                <div className="flex items-center gap-3">
                  <span className="font-mono-plex inline-flex h-7 w-7 items-center justify-center rounded-full bg-[color:var(--maroon)] text-[11px] font-bold text-white">
                    {i + 1}
                  </span>
                  <l.ikon className="h-4 w-4 text-[color:var(--maroon)]" />
                </div>
                <h3 className="font-display mt-4 text-base font-bold text-[color:var(--ink)]">
                  {l.tajuk}
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed text-[color:var(--ink-muted)]">
                  {l.copy}
                </p>
              </Dedah>
            ))}
          </ol>

          {/* Pratonton 5 peringkat */}
          <Dedah delay={0.1}>
            <div className={`${KAD} mt-6 p-5 sm:p-6`} data-testid="landing-status-tracker-preview">
              <p className={EYEBROW_LP}>Lima peringkat setiap laporan</p>
              <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:items-center">
                {PERINGKAT.map((p, i) => (
                  <React.Fragment key={p}>
                    <div className="flex items-center gap-2.5 sm:flex-col sm:gap-2 sm:text-center">
                      <span
                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-bold text-white"
                        style={{
                          backgroundColor: i === 4 ? "#0E9F6E" : "var(--brand-blue)",
                          opacity: 1 - i * 0.1,
                        }}
                      >
                        {i + 1}
                      </span>
                      <span className="text-xs font-semibold text-[color:var(--ink)]">{p}</span>
                    </div>
                    {i < PERINGKAT.length - 1 && (
                      <span
                        className="ml-3.5 h-4 w-px shrink-0 bg-[color:var(--hairline)] sm:ml-0 sm:h-px sm:flex-1"
                        aria-hidden="true"
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>
              <p className="mt-5 text-xs leading-relaxed text-[color:var(--ink-muted)]">
                Setiap peringkat direkod bersama tarikh dan catatan tindakan. Penutupan kes
                memerlukan bukti kerja atau catatan penutupan daripada pasukan.
              </p>
            </div>
          </Dedah>

          <Dedah>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link to="/aduan" className={BTN_UTAMA} data-testid="cara-cta-aduan">
                <MessageSquarePlus className="h-4 w-4" /> Mulakan Laporan
              </Link>
              <a href="#semak-status" className={BTN_KEDUA} data-testid="cara-cta-semak">
                Saya Sudah Ada Nombor Rujukan
              </a>
            </div>
          </Dedah>
        </Seksyen>
      </div>

      {/* ------------------------------------------------------- PRINSIP */}
      <Seksyen className="py-14 sm:py-16">
        <Dedah>
          <p className={EYEBROW_LP}>Cara kami mengurus</p>
          <h2 className="font-display mt-3 text-2xl font-semibold tracking-tight text-[color:var(--ink)] sm:text-3xl">
            Laporan anda diurus dengan lebih bertanggungjawab
          </h2>
        </Dedah>
        <div
          className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-4"
          data-testid="landing-trust-principles"
        >
          {PRINSIP.map((p, i) => (
            <Dedah key={p.tajuk} delay={i * 0.06}>
              <div className={`${KAD} h-full p-5`} data-testid={`prinsip-${i}`}>
                <span className={LENCANA_BIRU}>
                  <p.ikon className="h-[18px] w-[18px]" />
                </span>
                <h3 className="font-display mt-4 text-base font-bold text-[color:var(--ink)]">
                  {p.tajuk}
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed text-[color:var(--ink-muted)]">
                  {p.copy}
                </p>
              </div>
            </Dedah>
          ))}
        </div>
      </Seksyen>

      {/* -------------------------------------------------- SEMAK STATUS */}
      <div className="tona-awam border-y border-[color:var(--hairline)]">
        <Seksyen id="semak-status" className="py-14 sm:py-16">
          <div className="grid gap-8 lg:grid-cols-[0.9fr_1.1fr] lg:gap-14">
            <Dedah>
              <p className={EYEBROW_LP}>Semak status</p>
              <h2 className="font-display mt-3 text-2xl font-semibold tracking-tight text-[color:var(--ink)] sm:text-3xl">
                Sudah membuat laporan?
              </h2>
              <p className="mt-3 text-sm leading-relaxed text-[color:var(--ink-muted)]">
                Masukkan nombor rujukan penuh yang diberikan selepas laporan berjaya dihantar untuk
                melihat status terkini laporan anda.
              </p>
              <Jalur className="mt-6" />
            </Dedah>

            <Dedah delay={0.08}>
              <form onSubmit={semak} className={`${KAD} p-6`} data-testid="borang-semak-landing">
                <label
                  htmlFor="rujukan-landing"
                  className="block text-[11px] font-semibold uppercase tracking-[0.14em] text-[color:var(--ink-muted)]"
                >
                  Nombor rujukan laporan
                </label>
                <Input
                  id="rujukan-landing"
                  value={rujukan}
                  onChange={(e) => setRujukan(e.target.value)}
                  placeholder="Contoh: PR-2026-0042-K7QW9"
                  data-testid="input-rujukan-landing"
                  className="mt-2 h-12 rounded-xl border-[color:var(--hairline)] bg-white px-3.5 text-sm placeholder:text-[#6B7280] focus-visible:ring-2 focus-visible:ring-[color:var(--focus)]"
                />
                <button
                  type="submit"
                  className={`${BTN_BIRU} mt-4 w-full`}
                  data-testid="butang-semak-landing"
                >
                  <Search className="h-4 w-4" /> Semak Status Laporan
                </button>
                <p className="mt-3 text-xs leading-relaxed text-[color:var(--ink-muted)]">
                  Bagi melindungi privasi, nombor rujukan penuh termasuk kod pengesahan diperlukan
                  untuk melihat butiran laporan.
                </p>
              </form>
            </Dedah>
          </div>
        </Seksyen>
      </div>

      {/* ---------------------------------------------------- SOALAN LAZIM */}
      <Seksyen id="soalan" className="py-14 sm:py-16">
        <Dedah>
          <p className={EYEBROW_LP}>Soalan lazim</p>
          <h2 className="font-display mt-3 text-2xl font-semibold tracking-tight text-[color:var(--ink)] sm:text-3xl">
            Ada perkara yang ingin ditanya?
          </h2>
        </Dedah>

        <div className="mt-8 max-w-3xl">
          <Dedah>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#6B7280]" />
              <Input
                value={cariSoalan}
                onChange={(e) => setCariSoalan(e.target.value)}
                placeholder="Cari soalan, contohnya rujukan atau kecemasan"
                aria-label="Cari soalan lazim"
                data-testid="landing-faq-search"
                className="h-12 rounded-xl border-[color:var(--hairline)] bg-white pl-10 pr-3.5 text-sm placeholder:text-[#6B7280] focus-visible:ring-2 focus-visible:ring-[color:var(--focus)]"
              />
            </div>
          </Dedah>

          <Accordion type="single" collapsible className="mt-4 w-full" data-testid="landing-faq-accordion">
            {soalanTapis.map((s) => {
              const idx = SOALAN.indexOf(s);
              return (
                <AccordionItem
                  key={s.t}
                  value={`s-${idx}`}
                  className="border-b border-[color:var(--hairline)]"
                >
                  <AccordionTrigger
                    data-testid={`soalan-${idx}`}
                    className="py-4 text-left text-sm font-semibold text-[color:var(--ink)] hover:no-underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--focus)]"
                  >
                    {s.t}
                  </AccordionTrigger>
                  <AccordionContent className="pb-5 text-sm leading-relaxed text-[color:var(--ink-muted)]">
                    {s.j}
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>

          {soalanTapis.length === 0 && (
            <div className={`${KAD} mt-4 p-6`} data-testid="landing-faq-kosong">
              <p className="text-sm font-semibold text-[color:var(--ink)]">
                Tiada soalan yang sepadan
              </p>
              <p className="mt-1 text-sm text-[color:var(--ink-muted)]">
                Cuba kata kunci lain, atau hubungi kami melalui halaman{" "}
                <Link
                  to="/hubungi"
                  className="font-semibold text-[color:var(--maroon)] underline decoration-2 underline-offset-2"
                >
                  Hubungi Kami
                </Link>
                .
              </p>
            </div>
          )}
        </div>
      </Seksyen>

      {/* ------------------------------------------------- CTA PENUTUP */}
      <Seksyen className="pb-16 sm:pb-20">
        <Dedah>
          <div
            className="bintik-halus overflow-hidden rounded-2xl bg-[color:var(--maroon-gelap)] px-6 py-12 sm:px-10 sm:py-14"
            data-testid="landing-closing-cta"
          >
            <Jalur />
            <h2 className="font-display mt-6 max-w-2xl text-2xl font-semibold leading-tight tracking-tight text-white sm:text-3xl">
              Ada isu yang perlu diberi perhatian?
            </h2>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-[color:var(--krim)] sm:text-base">
              Berikan maklumat yang jelas supaya pasukan kami dapat memahami masalah dan menentukan
              langkah susulan yang sesuai.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link
                to="/aduan"
                data-testid="penutup-cta-aduan"
                className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl bg-[color:var(--krim)] px-5 text-sm font-semibold text-[color:var(--maroon-gelap)] transition-colors duration-200 hover:bg-white active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--krim)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--maroon-gelap)]"
              >
                <MessageSquarePlus className="h-4 w-4" /> Hantar Aduan Sekarang
              </Link>
              <Link
                to="/semak"
                data-testid="penutup-cta-semak"
                className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl border border-[rgba(243,234,214,0.42)] px-5 text-sm font-semibold text-[color:var(--krim)] transition-colors duration-200 hover:bg-[rgba(243,234,214,0.12)] active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--krim)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--maroon-gelap)]"
              >
                Semak Laporan Sedia Ada
              </Link>
            </div>
          </div>
        </Dedah>
      </Seksyen>

      {/* Bar tindakan pantas pada telefon */}
      <div
        className="fixed inset-x-0 bottom-0 z-40 flex gap-2 border-t border-[color:var(--hairline)] bg-white/95 px-4 py-3 backdrop-blur lg:hidden"
        data-testid="landing-bar-mobil"
      >
        <Link to="/aduan" className={`${BTN_UTAMA} flex-1`} data-testid="bar-mobil-aduan">
          <MessageSquarePlus className="h-4 w-4" /> Hantar Aduan
        </Link>
        <Link to="/semak" className={`${BTN_KEDUA} flex-1`} data-testid="bar-mobil-semak">
          <Search className="h-4 w-4" /> Semak
        </Link>
      </div>
      <div className="h-[76px] lg:hidden" aria-hidden="true" />
    </LamanAwam>
  );
}
